import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileDown, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { CycleReportTemplate } from './CycleReportTemplate';

export const CycleReportModal = ({ 
  isOpen, 
  onClose, 
  schedule, 
  studentName,
  cycleType
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  schedule: any[]; 
  studentName: string;
  cycleType: string;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all' or week number
  
  const reportRef = useRef<HTMLDivElement>(null);

  const generatePDF = async () => {
    if (!reportRef.current) return;
    setIsGenerating(true);
    try {
      const pages = reportRef.current.querySelectorAll('.pdf-page');
      if (pages.length === 0) {
        setIsGenerating(false);
        return;
      }
      
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        
        const canvas = await html2canvas(pages[i] as HTMLElement, {
          scale: 4,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL('image/png');
        
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = pdfWidth / imgWidth;
        const totalPdfHeight = imgHeight * ratio;
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        let heightLeft = totalPdfHeight;
        let position = 0;
        
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, totalPdfHeight);
        heightLeft -= pdfHeight;
        
        while (heightLeft > 0) {
          position = position - pdfHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, totalPdfHeight);
          heightLeft -= pdfHeight;
        }
      }

      pdf.save(`Relatorio_${cycleType}_${studentName.replace(/\s+/g, '_')}.pdf`);
      onClose();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar o relatório. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const filteredSchedule = periodFilter === 'all' 
    ? schedule 
    : schedule.filter(s => s.weekNumber === parseInt(periodFilter));

  const uniqueWeeks = Array.from(new Set(schedule.map(s => s.weekNumber))).sort((a: any, b: any) => a - b);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.8)', padding: '20px' }}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{ backgroundColor: '#1a1a1a', borderRadius: '24px', width: '100%', maxWidth: '500px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
            >
              <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', color: 'white', margin: 0 }}>Relatório de Ciclo</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'gray', cursor: 'pointer', padding: '8px' }}>
                  <X size={24} />
                </button>
              </div>

              <div style={{ padding: '24px', overflowY: 'auto' }}>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '8px' }}>Selecione o Período</label>
                  <select 
                    value={periodFilter} 
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <option value="all" style={{ color: 'black' }}>Ciclo Completo</option>
                    {uniqueWeeks.map(w => (
                      <option key={w} value={w} style={{ color: 'black' }}>Semana {w}</option>
                    ))}
                  </select>
                </div>

                <button 
                  onClick={generatePDF} 
                  disabled={isGenerating}
                  style={{ width: '100%', padding: '16px', borderRadius: '12px', background: isGenerating ? '#2A6E48' : '#3A8E58', color: 'black', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer' }}
                >
                  {isGenerating ? <><Loader2 size={20} className="animate-spin" /> Gerando PDF...</> : <><FileDown size={20} /> Baixar Relatório (PDF)</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Hidden PDF template to be rendered and captured */}
      {isOpen && (
        <CycleReportTemplate 
          ref={reportRef} 
          schedule={filteredSchedule} 
          studentName={studentName}
          cycleType={cycleType}
          periodText={periodFilter === 'all' ? 'Ciclo Completo' : `Semana ${periodFilter}`}
        />
      )}
    </>
  );
};
